import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  TextField,
  Divider,
  Card,
  CardContent,
  IconButton,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  Close,
  Person,
  Hotel,
  CalendarToday,
  CheckCircle,
  Key,
  CreditCard,
  Luggage,
} from '@mui/icons-material';
import { format } from 'date-fns';

export default function CheckInDialog({ open, onClose, booking, onCheckIn }) {
  const [formData, setFormData] = useState({
    idVerified: false,
    depositConfirmed: false,
    roomKeysIssued: false,
    luggageAssistance: false,
    actualCheckInTime: format(new Date(), 'HH:mm'),
    numberOfGuests: booking?.numberOfGuests || 1,
    vehicleNumber: '',
    specialInstructions: '',
  });

  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.idVerified) {
      newErrors.idVerified = 'Please verify guest ID before check-in';
    }
    if (!formData.depositConfirmed) {
      newErrors.depositConfirmed = 'Please confirm deposit payment';
    }
    if (!formData.roomKeysIssued) {
      newErrors.roomKeysIssued = 'Please confirm room keys have been issued';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCheckIn = () => {
    if (!validateForm()) return;

    const checkInData = {
      ...booking,
      status: 'Checked In',
      actualCheckInTime: formData.actualCheckInTime,
      actualCheckInDate: format(new Date(), 'yyyy-MM-dd'),
      numberOfGuests: formData.numberOfGuests,
      vehicleNumber: formData.vehicleNumber,
      checkInNotes: formData.specialInstructions,
    };

    onCheckIn(checkInData);
    handleClose();
  };

  const handleClose = () => {
    setFormData({
      idVerified: false,
      depositConfirmed: false,
      roomKeysIssued: false,
      luggageAssistance: false,
      actualCheckInTime: format(new Date(), 'HH:mm'),
      numberOfGuests: booking?.numberOfGuests || 1,
      vehicleNumber: '',
      specialInstructions: '',
    });
    setErrors({});
    onClose();
  };

  if (!booking) return null;

  const allChecksPassed = formData.idVerified && formData.depositConfirmed && formData.roomKeysIssued;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <CheckCircle color="primary" />
            <Box>
              <Typography variant="h6">Check-In Guest</Typography>
              <Typography variant="caption" color="text.secondary">
                Booking ID: {booking?.id}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Guest Information Summary */}
          <Grid item xs={12}>
            <Card sx={{ bgcolor: '#e3f2fd' }}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box display="flex" gap={1} alignItems="center" mb={1}>
                      <Person fontSize="small" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        Guest Name
                      </Typography>
                    </Box>
                    <Typography variant="body1" fontWeight="bold">
                      {booking?.guest}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box display="flex" gap={1} alignItems="center" mb={1}>
                      <Hotel fontSize="small" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        Room
                      </Typography>
                    </Box>
                    <Typography variant="body1" fontWeight="bold">
                      {booking?.room}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box display="flex" gap={1} alignItems="center" mb={1}>
                      <CalendarToday fontSize="small" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        Check-In Date
                      </Typography>
                    </Box>
                    <Typography variant="body1" fontWeight="bold">
                      {booking?.checkIn}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box display="flex" gap={1} alignItems="center" mb={1}>
                      <CalendarToday fontSize="small" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        Check-Out Date
                      </Typography>
                    </Box>
                    <Typography variant="body1" fontWeight="bold">
                      {booking?.checkOut}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Check-In Requirements */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Check-In Requirements
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            {(errors.idVerified || errors.depositConfirmed || errors.roomKeysIssued) && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Please complete all required check-in steps before proceeding.
              </Alert>
            )}

            <List>
              <ListItem>
                <ListItemIcon>
                  <Person color={formData.idVerified ? 'success' : 'action'} />
                </ListItemIcon>
                <ListItemText
                  primary="Verify Guest Identity"
                  secondary="Check government-issued ID and verify against booking"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.idVerified}
                      onChange={(e) => handleChange('idVerified', e.target.checked)}
                      color="success"
                    />
                  }
                  label="Verified"
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  <CreditCard color={formData.depositConfirmed ? 'success' : 'action'} />
                </ListItemIcon>
                <ListItemText
                  primary="Confirm Deposit Payment"
                  secondary={`Total Amount: ${booking?.amount} | Payment Status: ${booking?.paymentStatus || 'Pending'}`}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.depositConfirmed}
                      onChange={(e) => handleChange('depositConfirmed', e.target.checked)}
                      color="success"
                    />
                  }
                  label="Confirmed"
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  <Key color={formData.roomKeysIssued ? 'success' : 'action'} />
                </ListItemIcon>
                <ListItemText
                  primary="Issue Room Keys"
                  secondary="Provide room key cards and explain room features"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.roomKeysIssued}
                      onChange={(e) => handleChange('roomKeysIssued', e.target.checked)}
                      color="success"
                    />
                  }
                  label="Issued"
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  <Luggage color={formData.luggageAssistance ? 'success' : 'action'} />
                </ListItemIcon>
                <ListItemText
                  primary="Luggage Assistance"
                  secondary="Offer help with luggage (optional)"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.luggageAssistance}
                      onChange={(e) => handleChange('luggageAssistance', e.target.checked)}
                      color="success"
                    />
                  }
                  label="Provided"
                />
              </ListItem>
            </List>
          </Grid>

          {/* Additional Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Additional Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Check-In Time"
              type="time"
              value={formData.actualCheckInTime}
              onChange={(e) => handleChange('actualCheckInTime', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Number of Guests"
              type="number"
              value={formData.numberOfGuests}
              onChange={(e) => handleChange('numberOfGuests', parseInt(e.target.value) || 1)}
              inputProps={{ min: 1 }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Vehicle Number"
              value={formData.vehicleNumber}
              onChange={(e) => handleChange('vehicleNumber', e.target.value)}
              placeholder="Optional"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Special Instructions / Notes"
              multiline
              rows={3}
              value={formData.specialInstructions}
              onChange={(e) => handleChange('specialInstructions', e.target.value)}
              placeholder="Any special requests or notes for housekeeping..."
            />
          </Grid>

          {allChecksPassed && (
            <Grid item xs={12}>
              <Alert severity="success" icon={<CheckCircle />}>
                All check-in requirements completed! Ready to check in the guest.
              </Alert>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleCheckIn}
          disabled={!allChecksPassed}
          startIcon={<CheckCircle />}
        >
          Complete Check-In
        </Button>
      </DialogActions>
    </Dialog>
  );
}
